"use client";

import Image from "next/image";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { NewPricingCopy } from "./copy";
import type { IPricingSectionV2, IPricingSectionV3, GROQButton } from "@/sanity/lib/definitions";
import {
  Asset,
  PlatformId,
  PRODUCT_ORDER,
  ProductId,
  SizeId,
  VariantId,
  visibleSizesIn,
} from "@/lib/new-pricing/products";
import {
  catalogPlanAvailability,
  catalogQuizPlan,
  resolvePricing,
  type ResolvedPlatformCard,
} from "@/lib/new-pricing/fromSanity";
import { GroqImage } from "@/helpers/Image";
import { tierCtaToGroqButton } from "../pricingSectionV2/helpers";
import PricingPlatformsBlock from "../pricingSectionV2/PricingPlatformsBlock";
import { TradovatePlatformCard } from "./TradovatePlatformDialog";
import type { QuizPlanSelection } from "../pricingSectionV2/quiz/PlanQuizDialog";
import type { QuizProduct } from "../pricingSectionV2/quiz/planQuizConfig";
import PlanTable, { type Stage } from "./PlanTable";
import CompareDialog from "./CompareDialog";
import InfoTip from "./InfoTip";

import logoTradovate from "@/public/new-pricing/logo-tradovate.png";
import logoTradingview from "@/public/new-pricing/logo-tradingview.png";
import logoTradingviewCharting from "@/public/new-pricing/trading-view-charting.png";
import logoNinjatrader from "@/public/new-pricing/logo-ninjatrader.png";
import logoVolumetrica from "@/public/new-pricing/logo-volumetrica.png";
import logoMt5 from "@/public/new-pricing/logo-mt5.png";
import logoQuantower from "@/public/new-pricing/logo-quantower.svg";
import logoAtas from "@/public/new-pricing/logo-atas.png";
import logoDeepchart from "@/public/new-pricing/logo-deepchart.svg";

const PlanQuizDialog = dynamic(() => import("../pricingSectionV2/quiz/PlanQuizDialog"), {
  ssr: false,
});

const ease = [0.25, 0.1, 0.25, 1] as const;

/** Quiz product keys (Sanity: core/rapid/bolt) → spec product ids. */
const QUIZ_TO_PRODUCT: Record<QuizProduct, ProductId> = {
  core: "core",
  rapid: "rapid",
  bolt: "instant",
};

export default function NewPricingSection({
  section,
  sectionV3,
  language,
  fixedAsset,
  checkoutHrefOverride,
  hideHero,
  renderHero,
  sectionClassName,
  innerClassName,
}: {
  /** Legacy pricingSectionV2 doc — still feeds the platforms footer fallback. */
  section?: IPricingSectionV2;
  /** pricingSectionV3 doc — makes every number, string and logo editable. */
  sectionV3?: IPricingSectionV3;
  language?: string;
  /** Asset-specific pages: lock the section to one asset (toggle + quiz hidden). */
  fixedAsset?: Asset;
  /** Affiliate pages: every checkout CTA points to this URL (aff/coupon params). */
  checkoutHrefOverride?: string;
  /** Pages with their own hero above the section. */
  hideHero?: boolean;
  /** Pages that draw the eyebrow / title / sub themselves, from the same resolved copy. */
  renderHero?: (copy: NewPricingCopy) => ReactNode;
  /** Merged onto the <section>, e.g. to match a page's own vertical rhythm. */
  sectionClassName?: string;
  /** Merged onto the inner column (header ↔ controls gap). */
  innerClassName?: string;
}) {
  // Everything the section shows comes through this bundle: Sanity V3 values
  // merged over the code defaults (products.ts / copy.ts / bundled logos).
  const resolved = useMemo(() => {
    const base = resolvePricing(sectionV3, language);
    return checkoutHrefOverride ? { ...base, checkoutUrl: () => checkoutHrefOverride } : base;
  }, [sectionV3, language, checkoutHrefOverride]);
  const copy = resolved.copy;
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();

  // Opens on Futures > Tradovate > Core (or the page's fixed asset).
  const [asset, setAsset] = useState<Asset>(fixedAsset ?? "futures");
  const [platform, setPlatform] = useState<PlatformId>(resolved.defaultPlatform[fixedAsset ?? "futures"]);
  const [productId, setProductId] = useState<ProductId>("core");
  const [stage, setStage] = useState<Stage>("challenge");
  const [compareOpen, setCompareOpen] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizLoaded, setQuizLoaded] = useState(false);

  const product = resolved.catalog[asset][productId];
  const sizes = visibleSizesIn(resolved.catalog, asset, productId, platform, resolved.rapidFundedVariants);

  const track = useCallback(
    (event: string, props: Record<string, unknown> = {}) => {
      posthog?.capture(event, props);
      sendGTMEvent({ event, ...props });
    },
    [posthog],
  );

  const switchAsset = (a: Asset) => {
    setAsset(a);
    setPlatform(resolved.defaultPlatform[a]);
    setStage("challenge");
    track("new_pricing_market_change", { market: a });
  };

  const switchPlatform = (p: PlatformId) => {
    setPlatform(p);
    track("new_pricing_platform_change", { platform: p, market: asset });
  };

  /** Spec §5 — every Get Plan opens the checkout base URL with its presel slug. */
  const makeCta = useCallback(
    (presel: string): GROQButton | null =>
      tierCtaToGroqButton(
        { title: copy.cta.getPlan, href: resolved.checkoutUrl(presel), variant: "dark" },
        copy.cta.getPlan,
      ),
    [copy, resolved],
  );

  const onCheckout = useCallback(
    (presel: string, variant: VariantId, size: SizeId) => {
      track("new_pricing_checkout_click", {
        product: productId,
        size,
        variant,
        presel,
        stage,
        market: asset,
        platform,
      });
    },
    [track, productId, stage, asset, platform],
  );

  const openQuiz = () => {
    setQuizLoaded(true);
    setQuizOpen(true);
    track("new_pricing_quiz_start", { market: asset });
  };

  const handleQuizSeeDetails = (selection: QuizPlanSelection) => {
    setQuizOpen(false);
    track("new_pricing_quiz_complete", {
      product: selection.product,
      asset: selection.asset,
      tier: selection.tierKey,
    });
    if (selection.asset === "futures" || selection.asset === "cfd_crypto") {
      setAsset(selection.asset);
      setPlatform(resolved.defaultPlatform[selection.asset]);
    }
    const mapped = QUIZ_TO_PRODUCT[selection.product as QuizProduct];
    if (mapped) setProductId(mapped);
    setStage("challenge");
    window.setTimeout(() => {
      document
        .getElementById("new-pricing-plans")
        ?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
    }, 300);
  };

  const assetOrder: Asset[] = ["futures", "cfd_crypto"];

  return (
    <section className={cn("relative w-full bg-white px-[2rem] py-[10rem] lg:px-0 lg:py-[15rem]", sectionClassName)}>
      <div
        className={cn(
          "mx-auto flex w-[min(165.125rem,100%)] flex-col items-center gap-[2rem] lg:gap-[4rem]",
          innerClassName,
        )}>
        {/* ── Hero — hidden on pages that bring their own hero ── */}
        {renderHero
          ? renderHero(copy)
          : !hideHero && (
              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, ease }}
                className="flex flex-col items-center gap-[2.5rem] text-center">
                <span className="font-blacker text-[2rem] leading-[1.5] italic">{copy.heroEyebrow}</span>
                <h2 className="font-geist text-[5rem] leading-[1.15] font-medium tracking-[-0.05em] text-black lg:text-[7rem]">
                  {copy.heroTitleLine1}
                  <br />
                  <span className="relative inline-block">
                    <span
                      className="absolute inset-x-[-0.25em] bottom-[0.02em] h-[0.75em] bg-[#e2f3ef]"
                      aria-hidden="true"
                    />
                    <span className="relative font-blacker font-normal tracking-[-0.03em] text-[#003828] italic">
                      {copy.heroTitleAccent}
                    </span>
                  </span>
                </h2>
                <p className="max-w-[46rem] font-geist text-[2rem] leading-[1.33] text-black/70">{copy.heroSub}</p>
              </motion.div>
            )}

        <div
          id="new-pricing-plans"
          className="flex w-full scroll-mt-[12rem] flex-col gap-[2.5rem]">
          {/* ── Asset toggle — hidden when the page is locked to one asset ── */}
          {!fixedAsset && (
            <div className="flex w-full items-center justify-center gap-[0.75rem] self-center lg:w-auto">
              {assetOrder.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={asset === a}
                  onClick={() => switchAsset(a)}
                  className={cn(
                    "flex-1 rounded-[0.75rem] px-[3rem] py-[2rem] font-geist text-[2.5rem] leading-[1.3] font-medium whitespace-nowrap text-black transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a] lg:w-[31.75rem] lg:flex-none",
                    asset === a ? "border border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
                  )}>
                  {a === "futures" ? copy.markets.futures : copy.markets.cfdCrypto}
                </button>
              ))}
            </div>
          )}

          {/* ── Platform selector — FILTERS the matrix (spec §2.2) ── */}
          <PlatformSelector
            asset={asset}
            platform={platform}
            onSelect={switchPlatform}
            badgeNew={copy.badgeNew}
            cards={resolved.platformSelector[asset]}
          />

          {/* ── Product selector ── */}
          <div
            role="tablist"
            className="grid w-full grid-cols-1 gap-[1.25rem] md:grid-cols-3">
            {PRODUCT_ORDER.map((pid) => {
              const active = pid === productId;
              const pc = copy.products[pid];
              return (
                <button
                  key={pid}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setProductId(pid);
                    setStage("challenge");
                    track("new_pricing_family_change", { family: pid, market: asset });
                  }}
                  className={cn(
                    "relative flex items-center justify-between gap-[1.5rem] rounded-[0.75rem] p-[3rem] text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
                    active ? "border border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
                  )}>
                  <span className="flex items-center gap-[1.5rem]">
                    <span
                      className={cn(
                        " h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-full lg:flex hidden",
                        active ? "bg-[#228e71]" : "border-2 border-black/15 bg-white",
                      )}
                      aria-hidden="true">
                      {active && (
                        <svg
                          viewBox="0 0 18 18"
                          className="h-[2.25rem] w-[2.25rem]"
                          fill="none">
                          <path
                            d="M3.5 9.5l3.5 3.5 7.5-8"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="flex flex-wrap items-center gap-[1rem]">
                      <span className="font-geist text-[3rem] leading-[1.3] font-medium tracking-[-0.05em] whitespace-nowrap text-black">
                        {pc.name}
                      </span>
                      <span
                        className={cn(
                          "rounded-[0.5rem] px-[1.5rem] py-[0.5rem] font-geist text-[1.75rem] leading-[1.3] whitespace-nowrap transition-colors",
                          active ? "bg-[#1aa87f] text-white" : "border border-[#103037]/15 bg-white text-[#103037]",
                        )}>
                        {pc.tag}
                      </span>
                      {pid === "rapid" && (
                        <span className="rounded-[0.5rem] bg-[#1aa87f] px-[1.25rem] py-[0.25rem] font-geist text-[1.75rem] leading-[1.3] text-white">
                          {copy.badgeNew}
                        </span>
                      )}
                    </span>
                  </span>
                  <InfoTip
                    label={pc.name}
                    className="absolute top-[1.5rem] right-[1.5rem]"
                    side="left">
                    <span>{pc.info}</span>
                  </InfoTip>
                </button>
              );
            })}
          </div>

          {/* ── Pricing matrix ── */}
          <PlanTable
            asset={asset}
            product={product}
            sizes={sizes}
            stage={stage}
            onStageChange={(s) => {
              setStage(s);
              track("new_pricing_stage_change", { family: productId, stage: s });
            }}
            copy={copy}
            makeCta={makeCta}
            onCheckout={onCheckout}
            onSizeChange={(size) => track("new_pricing_size_change", { family: productId, size, market: asset })}
            discounted={resolved.discounted}
            mostPopularSize={resolved.mostPopularSize}
          />

          {/* ── Quiz + compare bar ── */}
          <div className="flex w-full flex-col gap-[2.5rem] rounded-[1rem] bg-[#f4f4f4] py-[2.5rem] pr-[2.5rem] pl-[2.5rem] lg:flex-row lg:items-center lg:justify-between lg:pl-[4rem]">
            <div className="flex flex-col gap-[1rem]">
              <p className="font-geist text-[3.25rem] leading-[1.2] font-medium tracking-[-0.03em] text-black">
                {copy.unsure.title}
              </p>
              <p className="font-geist text-[2rem] leading-[1.35] text-black/60">{copy.unsure.sub}</p>
            </div>
            <div className="flex flex-col gap-[0.75rem] sm:flex-row">
              {/* on asset-locked pages the quiz skips the asset question */}
              {(section || sectionV3) && (
                <button
                  type="button"
                  onClick={openQuiz}
                  className="flex items-center justify-center gap-[1rem] rounded-[0.75rem] bg-white px-[3rem] py-[2rem] font-geist text-[2.5rem] leading-[1.3] font-medium text-black transition-colors hover:bg-[#e2f3ef] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]">
                  <QuestionIcon className="h-[3rem] w-[3rem]" />
                  {copy.unsure.helpMeChoose}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setCompareOpen(true);
                  track("new_pricing_comparison_open", { family: productId, market: asset });
                }}
                className="flex items-center justify-center gap-[1rem] rounded-[0.75rem] bg-white px-[3rem] py-[2rem] font-geist text-[2.5rem] leading-[1.3] font-medium text-black transition-colors hover:bg-[#e2f3ef] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]">
                <TableIcon className="h-[3rem] w-[3rem]" />
                {copy.unsure.compare}
              </button>
            </div>
          </div>
        </div>

        {/* ── Platforms section — same component as the live site (Sanity-driven,
            with platform links and popups). V3's footer wins when filled. ── */}
        {(() => {
          const footer = sectionV3?.platformsFooter?.groups?.length
            ? sectionV3.platformsFooter
            : section?.platformsFooter;
          return footer ? (
            <PricingPlatformsBlock
              data={footer}
              language={language}
              // asset-locked pages only show their own asset's platforms
              filter={fixedAsset ? { assetKey: fixedAsset } : undefined}
              groupExtras={{
                futures: <TradovatePlatformCard language={language} />,
              }}
            />
          ) : null;
        })()}
      </div>

      <CompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        initialAsset={asset}
        fixedAsset={fixedAsset}
        copy={copy}
        resolved={resolved}
        onCheckout={(presel, ref) =>
          track("new_pricing_checkout_click", {
            source: "compare",
            product: ref.product,
            size: ref.size,
            variant: ref.variant,
            presel,
            market: ref.asset,
          })
        }
      />

      {quizLoaded && (section || sectionV3) && (
        <PlanQuizDialog
          open={quizOpen}
          onOpenChange={setQuizOpen}
          data={section}
          language={language}
          activeAssetKey={asset}
          fixedAsset={fixedAsset}
          onSeeDetails={handleQuizSeeDetails}
          availability={catalogPlanAvailability(resolved.catalog)}
          assetLabels={{ futures: copy.markets.futures, cfd_crypto: copy.markets.cfdCrypto }}
          resolveCatalogPlan={(product, quizAsset, size) => catalogQuizPlan(resolved, product, quizAsset, size)}
        />
      )}
    </section>
  );
}

/* ── Platform selector — selection filters the matrix (spec §2.2) ── */

function PlatformSelector({
  asset,
  platform,
  onSelect,
  badgeNew,
  cards,
}: {
  asset: Asset;
  platform: PlatformId;
  onSelect: (p: PlatformId) => void;
  badgeNew: string;
  /** Sanity-driven cards (pricingSectionV3) — absent = bundled hard-coded cards. */
  cards?: ResolvedPlatformCard[];
}) {
  const cardCls = (active: boolean) =>
    cn(
      "relative flex flex-1 cursor-pointer flex-col items-center justify-center gap-[1.5rem] rounded-[1rem] px-[2.25rem] py-[1.75rem] text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
      active ? "border border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
    );

  // Vertical separator between logo groups — visible on mobile (Figma
  // 6206:4629), transparent from md up (Figma 6084:364/366/376/380).
  const vSep = (
    <span
      className="h-[3.75rem] w-px shrink-0 bg-black/0 md:h-[4.5rem] md:bg-white/0"
      aria-hidden="true"
    />
  );

  // Sanity-driven cards (pricingSectionV3): one generic layout — title,
  // dashed divider, logo row — covering both Figma card styles. Logos are
  // Sanity images with an optional label and width.
  if (cards?.length) {
    return (
      <div className="flex w-full flex-col gap-[1rem] md:flex-row md:items-stretch">
        {cards.map((card) => (
          <button
            key={card.key}
            type="button"
            aria-pressed={platform === card.key}
            onClick={() => onSelect(card.key)}
            // fixed height: every card in a group is the same size (client
            // feedback — mobile rows and the CFD/Futures cards must match)
            className={cn(cardCls(platform === card.key), "h-[8.75rem] md:h-[11rem]")}>
            {card.badgeNew && (
              <span className="absolute top-[1.125rem] right-[1.25rem] rounded-[0.5rem] bg-[#1aa87f] px-[1.25rem] py-[0.25rem] font-geist text-[2rem] leading-[1.3] tracking-[-0.05em] text-white">
                {badgeNew}
              </span>
            )}
            {/* <span className="font-geist text-[2rem] leading-[1.3] text-black">{card.title}</span> */}
            {/* <span */}
            {/*   className="h-px w-full border-t border-dashed border-black/15" */}
            {/*   aria-hidden="true" */}
            {/* /> */}
            <span
              className={cn(
                "flex items-center",
                // single logos (e.g. MT5) always centered; multi-logo rows
                // spread on mobile, centered from md up
                card.logos.length > 1
                  ? "w-full justify-between gap-[1.5rem] md:w-auto md:justify-center md:gap-[2rem] lg:gap-[4rem]"
                  : "justify-center gap-[1rem]",
              )}>
              {card.logos.map((logo, i) => (
                <span
                  key={logo.key}
                  className="contents">
                  {/* separator between logos — except after Volumetrica, where the "+" sits */}
                  {i > 0 && card.logos[i - 1]?.label !== "Volumetrica" && vSep}
                  <span className="flex items-center gap-[0.75rem] md:gap-[1rem]">
                    {logo.image && (
                      <GroqImage
                        src={logo.image}
                        alt={logo.label ?? ""}
                        className="object-contain"
                        // width from Studio, height capped so no logo can blow
                        // a card up (Volumetrica-too-big complaint)
                        style={{ width: `${logo.widthRem ?? 4}rem`, height: "auto", maxHeight: "5rem" }}
                      />
                    )}

                    {logo.label && (
                      <span className="font-geist text-[2.1rem] leading-[1.06] font-medium tracking-[-0.03em] text-black md:text-[2.5rem]">
                        {logo.label}
                      </span>
                    )}
                  </span>

                  {logo.label === "Volumetrica" && (
                    <svg
                      width="27"
                      height="27"
                      viewBox="0 0 27 27"
                      fill="none"
                      className="lg:-mx-[2rem]"
                      xmlns="http://www.w3.org/2000/svg">
                      <rect
                        width="26.6667"
                        height="26.6667"
                        transform="matrix(1 0 0 -1 0 26.667)"
                        fill="white"
                        fill-opacity="0.01"
                      />
                      <path
                        d="M14.1665 14.1667V20H12.4998V14.1667H6.6665V12.5H12.4998V6.66667H14.1665V12.5H19.9998V14.1667H14.1665Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </span>
              ))}
            </span>
          </button>
        ))}
      </div>
    );
  }

  if (asset === "futures") {
    // 1:1 with Figma node 6084:357 — Tradovate card left, dxFeed card right;
    // images sized by width only so their intrinsic ratio holds.
    return (
      <div className="flex w-full flex-col gap-[1rem] md:flex-row md:items-stretch">
        <button
          type="button"
          aria-pressed={platform === "tradovate"}
          onClick={() => onSelect("tradovate")}
          className={cardCls(platform === "tradovate")}>
          <span className="absolute top-[1.125rem] right-[1.25rem] rounded-[0.5rem] bg-[#1aa87f] px-[1.25rem] py-[0.25rem] font-geist text-[2rem] leading-[1.3] tracking-[-0.05em] text-white">
            {badgeNew}
          </span>
          <span className="font-geist text-[2rem] leading-[1.3] text-black">Tradovate</span>
          <span
            className="h-px w-full border-t border-dashed border-black/15"
            aria-hidden="true"
          />
          <span className="flex w-full items-center justify-between gap-[1.5rem] md:w-auto md:justify-center md:gap-[2rem] lg:gap-[5rem]">
            <Image
              src={logoTradovate}
              alt="Tradovate"
              className="h-auto w-[11.5rem] md:w-[14.5rem]"
            />
            {vSep}
            <Image
              src={logoTradingview}
              alt="TradingView"
              className="h-auto w-[14rem] md:w-[17.66rem]"
            />
            {vSep}
            <Image
              src={logoNinjatrader}
              alt="NinjaTrader"
              className="h-auto w-[7.5rem] md:w-[9.29rem]"
            />
          </span>
        </button>
        <button
          type="button"
          aria-pressed={platform === "dxfeed"}
          onClick={() => onSelect("dxfeed")}
          className={cardCls(platform === "dxfeed")}>
          <span className="font-geist text-[2rem] leading-[1.3] text-black">dxFeed</span>
          <span
            className="h-px w-full border-t border-dashed border-black/15"
            aria-hidden="true"
          />
          <span className="flex w-full items-center justify-between gap-[1.5rem] md:w-auto md:justify-center md:gap-[2rem] lg:gap-[5rem]">
            <span className="flex items-center gap-[0.75rem] md:gap-[1rem]">
              <Image
                src={logoQuantower}
                alt=""
                className="h-auto w-[3rem] md:w-[3.5rem]"
              />
              <span className="font-geist text-[2.1rem] leading-[1.06] font-medium tracking-[-0.03em] text-black capitalize md:text-[2.5rem]">
                Quantower
              </span>
            </span>
            {vSep}
            <span className="flex items-center gap-[0.75rem] md:gap-[1rem]">
              <Image
                src={logoAtas}
                alt=""
                className="h-auto w-[2.875rem] rounded-[0.625rem] md:w-[3.375rem]"
              />
              <span className="font-geist text-[2.1rem] leading-[1.06] font-medium tracking-[-0.03em] text-black md:text-[2.5rem]">
                ATAS
              </span>
            </span>
            {vSep}
            <span className="flex items-center gap-[0.75rem] md:gap-[1rem]">
              <Image
                src={logoDeepchart}
                alt=""
                className="h-auto w-[3.75rem] md:w-[4.5rem]"
              />
              <span className="font-geist text-[2.1rem] leading-[1.06] font-medium tracking-[-0.03em] text-black capitalize md:text-[2.5rem]">
                Deepchart
              </span>
            </span>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-1 gap-[1rem] md:grid-cols-2">
      <button
        type="button"
        aria-pressed={platform === "mt5"}
        onClick={() => onSelect("mt5")}
        className={cn(cardCls(platform === "mt5"), "flex-row")}>
        <span className="absolute top-[1rem] right-[1rem] rounded-[0.5rem] bg-[#1aa87f] px-[1.25rem] py-[0.25rem] font-geist text-[1.75rem] leading-[1.3] text-white">
          {badgeNew}
        </span>
        <Image
          src={logoMt5}
          alt=""
          className="h-[4rem] w-auto object-contain"
        />
        <span className="font-geist text-[2.25rem] font-medium text-black">MetaTrader 5</span>
      </button>
      <button
        type="button"
        aria-pressed={platform === "volumetrica"}
        onClick={() => onSelect("volumetrica")}
        className={cn(cardCls(platform === "volumetrica"), "flex-row gap-[2rem]")}>
        <span className="flex items-center gap-[1rem]">
          <Image
            src={logoVolumetrica}
            alt=""
            className="h-[4rem] w-auto object-contain"
          />
          <span className="font-geist text-[2.25rem] font-medium text-black">Volumetrica</span>
        </span>

        <svg
          width="27"
          height="27"
          viewBox="0 0 27 27"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <rect
            width="26.6667"
            height="26.6667"
            transform="matrix(1 0 0 -1 0 26.667)"
            fill="white"
            fill-opacity="0.01"
          />
          <path
            d="M14.1665 14.1667V20H12.4998V14.1667H6.6665V12.5H12.4998V6.66667H14.1665V12.5H19.9998V14.1667H14.1665Z"
            fill="black"
          />
        </svg>

        <span className="flex items-center gap-[1rem]">
          <Image
            src={logoTradingviewCharting}
            alt=""
            quality={100}
            className="w-[min(18rem)] object-contain"
          />
        </span>
      </button>
    </div>
  );
}

function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 1.5C9.9233 1.5 7.89323 2.11581 6.16652 3.26957C4.4398 4.42332 3.09399 6.0632 2.29927 7.98182C1.50455 9.90045 1.29661 12.0116 1.70176 14.0484C2.1069 16.0852 3.10693 17.9562 4.57538 19.4246C6.04383 20.8931 7.91476 21.8931 9.95156 22.2982C11.9884 22.7034 14.0996 22.4955 16.0182 21.7007C17.9368 20.906 19.5767 19.5602 20.7304 17.8335C21.8842 16.1068 22.5 14.0767 22.5 12C22.5 9.21523 21.3938 6.54451 19.4246 4.57538C17.4555 2.60625 14.7848 1.5 12 1.5ZM12 21C10.22 21 8.47992 20.4722 6.99987 19.4832C5.51983 18.4943 4.36628 17.0887 3.68509 15.4442C3.0039 13.7996 2.82567 11.99 3.17294 10.2442C3.5202 8.49836 4.37737 6.89471 5.63604 5.63604C6.89472 4.37737 8.49836 3.5202 10.2442 3.17293C11.99 2.82567 13.7996 3.0039 15.4442 3.68508C17.0887 4.36627 18.4943 5.51983 19.4832 6.99987C20.4722 8.47991 21 10.22 21 12C21 14.3869 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.387 21 12 21Z"
        fill="#228E71"
      />
      <path
        d="M12 18.75C12.6213 18.75 13.125 18.2463 13.125 17.625C13.125 17.0037 12.6213 16.5 12 16.5C11.3787 16.5 10.875 17.0037 10.875 17.625C10.875 18.2463 11.3787 18.75 12 18.75Z"
        fill="#228E71"
      />
      <path
        d="M12.75 6H11.625C11.1815 5.99901 10.7422 6.08563 10.3323 6.25489C9.92236 6.42415 9.54991 6.67272 9.23631 6.98631C8.92272 7.2999 8.67416 7.67235 8.5049 8.08227C8.33564 8.4922 8.24902 8.93151 8.25 9.375V9.75H9.75V9.375C9.75 8.87772 9.94755 8.40081 10.2992 8.04917C10.6508 7.69754 11.1277 7.5 11.625 7.5H12.75C13.2473 7.5 13.7242 7.69754 14.0758 8.04917C14.4275 8.40081 14.625 8.87772 14.625 9.375C14.625 9.87228 14.4275 10.3492 14.0758 10.7008C13.7242 11.0525 13.2473 11.25 12.75 11.25H11.25V14.625H12.75V12.75C13.6451 12.75 14.5036 12.3944 15.1365 11.7615C15.7694 11.1285 16.125 10.2701 16.125 9.375C16.125 8.47989 15.7694 7.62145 15.1365 6.98851C14.5036 6.35558 13.6451 6 12.75 6Z"
        fill="#228E71"
      />
    </svg>
  );
}

function TableIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20.25 2.25H3.75C3.35218 2.25 2.97064 2.40804 2.68934 2.68934C2.40804 2.97064 2.25 3.35218 2.25 3.75V20.25C2.25 20.6478 2.40804 21.0294 2.68934 21.3107C2.97064 21.592 3.35218 21.75 3.75 21.75H20.25C20.6478 21.75 21.0294 21.592 21.3107 21.3107C21.592 21.0294 21.75 20.6478 21.75 20.25V3.75C21.75 3.35218 21.592 2.97064 21.3107 2.68934C21.0294 2.40804 20.6478 2.25 20.25 2.25ZM20.25 3.75V6.75H3.75V3.75H20.25ZM12.75 8.25H20.25V13.5H12.75V8.25ZM11.25 13.5H3.75V8.25H11.25V13.5ZM3.75 15H11.25V20.25H3.75V15ZM12.75 20.25V15H20.25V20.25H12.75Z"
        fill="#228E71"
      />
    </svg>
  );
}
